import React from "react";
import { Autocomplete, Box, IconButton, TextField, Tooltip, Typography } from "@mui/material";
import {
  AddCircleOutline,
  Delete,
  LocalOffer,
  LocalOfferOutlined,
  PropaneTankOutlined,
} from "@mui/icons-material";
import BotonMarcaLinea from "./BotonMarcaLinea";
import { esCantidadValida, esPromoOSena } from "../utils/lineas";

// Teclas que un input type="number" acepta pero nunca forman un entero >= 1.
const TECLAS_NO_ENTERAS = ["e", "E", "+", "-", ".", ","];

const COLOR_PROMO = {
  main: "primary.main",
  dark: "primary.dark",
  rgb: "25, 118, 210",
  hoverBorder: "#1976d2",
};

// Mismo color que el botón "+ Envases" de la app.
const COLOR_ENVASE = "#1A7F8E";
const COLOR_ENVASE_RGB = "26, 127, 142";

const TOOLTIPS_PROMO_SENA = {
  bloqueado: "Ya existe una promo / seña para este producto",
  activo: "Quitar promo / seña",
  inactivo: "Marcar como promo / seña",
};

const bordeFoco = { borderColor: "#1976d2", borderWidth: "2px" };

const estiloRootInput = {
  height: "40px",
  backgroundColor: "white",
  borderRadius: "6px",
  "&:hover .MuiOutlinedInput-notchedOutline": bordeFoco,
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": bordeFoco,
};

const estiloLabel = { "& .MuiInputLabel-root": { fontSize: "0.875rem" } };

const estiloInput = {
  "& .MuiOutlinedInput-root": { ...estiloRootInput, fontSize: "0.875rem" },
  ...estiloLabel,
};

// El input de cantidad del original no fija fontSize en el root (lo hace en inputProps).
const estiloInputCantidad = {
  "& .MuiOutlinedInput-root": estiloRootInput,
  ...estiloLabel,
};

// Con error el borde queda rojo también en hover/foco (el estilo base lo pone azul).
const estiloInputCantidadError = {
  "& .MuiOutlinedInput-root": {
    ...estiloRootInput,
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "error.main", borderWidth: "2px" },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "error.main",
      borderWidth: "2px",
    },
  },
  ...estiloLabel,
};

const estiloBotonLinea = (color, sombra) => ({
  width: "40px",
  height: "40px",
  borderRadius: "6px",
  border: "2px solid",
  borderColor: `${color}.light`,
  backgroundColor: "white",
  "&:hover": {
    backgroundColor: `${color}.50`,
    borderColor: `${color}.main`,
    transform: "translateY(-1px)",
    boxShadow: sombra,
  },
  transition: "all 0.2s ease",
});

// COLOR_ENVASE no está en la paleta del tema, por eso va en hex.
const estiloBotonEnvase = {
  width: "40px",
  height: "40px",
  borderRadius: "6px",
  border: "2px solid",
  borderColor: `rgba(${COLOR_ENVASE_RGB}, 0.5)`,
  backgroundColor: "white",
  "&:hover": {
    backgroundColor: `rgba(${COLOR_ENVASE_RGB}, 0.08)`,
    borderColor: COLOR_ENVASE,
    transform: "translateY(-1px)",
    boxShadow: `0 2px 4px rgba(${COLOR_ENVASE_RGB}, 0.2)`,
  },
  transition: "all 0.2s ease",
};

// Ocupa el lugar del botón Promo / Seña en las líneas de envase.
const EtiquetaEnvase = () => (
  <Tooltip title="Envase: no admite promo / seña. El valor lo carga el chofer al entregar" arrow>
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        p: 1.5,
        width: "76px",
        height: "25px",
        borderRadius: "6px",
        border: "2px solid",
        borderColor: COLOR_ENVASE,
        backgroundColor: `rgba(${COLOR_ENVASE_RGB}, 0.12)`,
      }}
    >
      <PropaneTankOutlined sx={{ fontSize: 22, color: COLOR_ENVASE }} />
      <Typography
        variant="caption"
        sx={{ mt: 0.5, color: COLOR_ENVASE, fontWeight: 700, fontSize: "0.7rem", lineHeight: 1 }}
      >
        ENVASE
      </Typography>
    </Box>
  </Tooltip>
);

// Una línea del pedido: promo/seña, producto, cantidad, especificaciones y
// botones de eliminar / agregar línea.
export default function LineaPedido({
  linea,
  index,
  esUltima,
  puedeEliminar,
  esEnvase,
  productos,
  productoSeleccionado,
  hayOtraPromo,
  onPromoSenaChange,
  onProductoChange,
  onCantidadChange,
  onEspecificacionesChange,
  onEliminar,
  onAgregar,
  onAgregarEnvase,
}) {
  const esPromo = !esEnvase && esPromoOSena(linea);
  // Siempre se puede desmarcar; marcar se bloquea si otra línea del mismo
  // producto ya es promo / seña.
  const promoBloqueada = !esPromo && hayOtraPromo;
  const cantidadInvalida = !esCantidadValida(linea.cantidad);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        p: 1.5,
        mb: 1,
        borderRadius: "8px",
        backgroundColor: esEnvase
          ? `rgba(${COLOR_ENVASE_RGB}, 0.06)`
          : esPromo
            ? "rgba(25, 118, 210, 0.06)"
            : "#f8f9fa",
        border: "1px solid",
        borderColor: esEnvase ? COLOR_ENVASE : esPromo ? "primary.main" : "#e0e0e0",
        transition: "all 0.2s ease",
        "&:hover": {
          backgroundColor: esEnvase
            ? `rgba(${COLOR_ENVASE_RGB}, 0.1)`
            : esPromo
              ? "rgba(25, 118, 210, 0.1)"
              : "#f0f2f5",
          borderColor: esEnvase ? COLOR_ENVASE : esPromo ? "primary.dark" : "#bdbdbd",
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        },
      }}
    >
      <Box sx={{ display: "flex", minWidth: "100px" }}>
        {esEnvase ? (
          <EtiquetaEnvase />
        ) : (
          <BotonMarcaLinea
            label="Promo / Seña"
            ancho="76px"
            activo={esPromo}
            bloqueado={promoBloqueada}
            tooltips={TOOLTIPS_PROMO_SENA}
            color={COLOR_PROMO}
            IconoActivo={LocalOffer}
            IconoInactivo={LocalOfferOutlined}
            onToggle={(checked) => onPromoSenaChange(index, checked)}
          />
        )}
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        {productos && (
          <Autocomplete
            disabled={!productos}
            disableClearable
            size="small"
            required
            noOptionsText={esEnvase ? "Envase no encontrado" : "Producto no encontrado"}
            options={productos}
            autoHighlight
            // Los envases no tienen código de producto: se muestra sólo el nombre.
            getOptionLabel={(producto) =>
              esEnvase ? producto.nombre : `${producto.codigoproducto} - ${producto.nombre}`
            }
            value={productoSeleccionado}
            onChange={(event, value) => onProductoChange(index, value)}
            renderOption={(props, option) => (
              <Box
                component="li"
                key={option.id}
                sx={{
                  py: 0.75,
                  px: 1.5,
                  fontSize: "0.875rem",
                  "&:hover": { backgroundColor: "#f5f5f5" },
                }}
                {...props}
              >
                {esEnvase ? (
                  <Box component="span">{option.nombre}</Box>
                ) : (
                  <>
                    <Box component="span" sx={{ fontWeight: 500, mr: 1 }}>
                      {option.codigoproducto}
                    </Box>
                    <Box component="span" sx={{ color: "text.secondary" }}>
                      {option.nombre}
                    </Box>
                  </>
                )}
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                required
                size="small"
                placeholder={esEnvase ? "Buscar envase..." : "Buscar producto..."}
                variant="outlined"
                sx={estiloInput}
              />
            )}
          />
        )}
      </Box>

      <Box sx={{ width: "110px" }}>
        <Tooltip
          title={cantidadInvalida ? "La cantidad debe ser un número entero mayor o igual a 1" : ""}
          arrow
        >
          <TextField
            label="Cantidad"
            size="small"
            type="number"
            required
            error={cantidadInvalida}
            inputProps={{
              min: 1,
              step: 1,
              style: { textAlign: "center", fontSize: "0.875rem" },
            }}
            value={linea.cantidad || ""}
            onKeyDown={(e) => {
              if (TECLAS_NO_ENTERAS.includes(e.key)) e.preventDefault();
            }}
            onChange={(e) => onCantidadChange(index, e.target.value)}
            sx={cantidadInvalida ? estiloInputCantidadError : estiloInputCantidad}
          />
        </Tooltip>
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <TextField
          label="Especificaciones"
          size="small"
          fullWidth
          autoComplete="off"
          value={linea.especificaciones || ""}
          onChange={(e) => onEspecificacionesChange(index, e.target.value)}
          sx={estiloInput}
        />
      </Box>

      <Box sx={{ display: "flex", gap: 0.5, minWidth: "124px", justifyContent: "flex-end" }}>
        {puedeEliminar && (
          <Tooltip title="Eliminar línea" arrow>
            <IconButton
              size="small"
              onClick={() => onEliminar(index)}
              sx={estiloBotonLinea("error","0 2px 4px rgba(211, 47, 47, 0.2)")}
            >
              <Delete fontSize="small" sx={{ color: "error.main", fontSize: "20px" }} />
            </IconButton>
          </Tooltip>
        )}

        {esUltima && (
          <Tooltip title="Agregar línea" arrow>
            <IconButton
              size="small"
              onClick={() => onAgregar()}
              sx={estiloBotonLinea("primary","0 2px 4px rgba(25, 118, 210, 0.2)")}
            >
              <AddCircleOutline fontSize="small" sx={{ color: "primary.main", fontSize: "20px" }} />
            </IconButton>
          </Tooltip>
        )}

        {esUltima && (
          <Tooltip title="Agregar envase" arrow>
            <IconButton size="small" onClick={() => onAgregarEnvase()} sx={estiloBotonEnvase}>
              <PropaneTankOutlined fontSize="small" sx={{ color: COLOR_ENVASE, fontSize: "20px" }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
}
